import { ref, toRef, computed, defineComponent, Transition, inject, onBeforeUnmount } from 'vue';
import { BAR_MAP, renderThumbStyle } from './utils';
import { scrollbarContextKey } from './constants';
import { useEventListener } from '../../_util/hooks/_vueuse/useEventListener';
import classNames from '../../_util/classNames';
import useConfigInject from '../../_util/hooks/useConfigInject';
const Thumb = defineComponent({
    props: {
        vertical: Boolean,
        size: String,
        move: Number,
        ratio: {
            type: Number,
            required: true,
        },
        always: Boolean,
    },
    emits: [],
    setup(props) {
        const { prefixCls } = useConfigInject('scrollbar', props);
        const scrollbar = inject(scrollbarContextKey);
        const instance = ref();
        const thumb = ref();
        const thumbState = ref({});
        const visible = ref(false);
        let cursorDown = false;
        let cursorLeave = false;
        let originalOnSelectStart = document.onselectstart;
        const bar = computed(() => BAR_MAP[props.vertical ? 'vertical' : 'horizontal']);
        const offsetRatio = computed(() => 
        // offsetRatioX = original width of thumb / current width of thumb / ratioX
        // offsetRatioY = original height of thumb / current height of thumb / ratioY
        // instance height = wrap height - GAP
        instance.value[bar.value.offset] ** 2 /
            scrollbar.wrapElement[bar.value.scrollSize] /
            props.ratio /
            thumb.value[bar.value.offset]);
        const thumbStyle = computed(() => renderThumbStyle({
            size: props.size,
            move: props.move,
            bar: bar.value,
        }));
        const startDrag = (e) => {
            e.stopImmediatePropagation();
            cursorDown = true;
            document.addEventListener('mousemove', mouseMoveDocumentHandler);
            document.addEventListener('mouseup', mouseUpDocumentHandler);
            originalOnSelectStart = document.onselectstart;
            document.onselectstart = () => false;
        };
        const mouseMoveDocumentHandler = (e) => {
            if (!instance.value || !thumb.value)
                return;
            if (cursorDown === false)
                return;
            const prevPage = thumbState.value[bar.value.axis];
            if (!prevPage)
                return;
            const offset = (instance.value.getBoundingClientRect()[bar.value.direction] - e[bar.value.client]) * -1;
            const thumbClickPosition = thumb.value[bar.value.offset] - prevPage;
            const thumbPositionPercentage = ((offset - thumbClickPosition) * 100 * offsetRatio.value) /
                instance.value[bar.value.offset];
            scrollbar.wrapElement[bar.value.scroll] =
                (thumbPositionPercentage * scrollbar.wrapElement[bar.value.scrollSize]) / 100;
        };
        const mouseUpDocumentHandler = () => {
            cursorDown = false;
            thumbState.value[bar.value.axis] = 0;
            document.removeEventListener('mousemove', mouseMoveDocumentHandler);
            document.removeEventListener('mouseup', mouseUpDocumentHandler);
            restoreOnselectstart();
            if (cursorLeave)
                visible.value = false;
        };
        const mouseMoveScrollbarHandler = () => {
            cursorLeave = false;
            visible.value = !!props.size;
        };
        const mouseLeaveScrollbarHandler = () => {
            cursorLeave = true;
            visible.value = cursorDown;
        };
        onBeforeUnmount(() => {
            restoreOnselectstart();
        });
        const restoreOnselectstart = () => {
            if (document.onselectstart !== originalOnSelectStart)
                document.onselectstart = originalOnSelectStart;
        };
        const clickTrackHandler = (e) => {
            if (!thumb.value || !instance.value || !scrollbar.wrapElement)
                return;
            const offset = Math.abs(e.target.getBoundingClientRect()[bar.value.direction] -
                e[bar.value.client]);
            const thumbHalf = thumb.value[bar.value.offset] / 2;
            const thumbPositionPercentage = ((offset - thumbHalf) * 100 * offsetRatio.value) / instance.value[bar.value.offset];
            scrollbar.wrapElement[bar.value.scroll] =
                (thumbPositionPercentage * scrollbar.wrapElement[bar.value.scrollSize]) / 100;
        };
        const clickThumbHandler = (e) => {
            e.stopPropagation();
            e.preventDefault(); // 阻止默认行为 例如失去焦点
            if (e.ctrlKey || [1, 2].includes(e.button))
                return;
            window.getSelection()?.removeAllRanges();
            startDrag(e);
            const el = e.currentTarget;
            if (!el)
                return;
            thumbState.value[bar.value.axis] =
                el[bar.value.offset] -
                    (e[bar.value.client] - el.getBoundingClientRect()[bar.value.direction]);
        };
        useEventListener(toRef(scrollbar, 'scrollbarElement'), 'mousemove', mouseMoveScrollbarHandler);
        useEventListener(toRef(scrollbar, 'scrollbarElement'), 'mouseleave', mouseLeaveScrollbarHandler);
        return () => {
            // const thumbStyle = {};
            const { always } = props;
            const barClassName = classNames({
                [`${prefixCls.value}-bar`]: true,
                [`is-${bar.value.key}`]: true,
            });
            const thumbClassName = classNames({
                [`${prefixCls.value}-thumb`]: true,
            });
            return (<Transition>
          <div v-show={always || visible.value} class={barClassName} ref={instance} onMousedown={clickTrackHandler}>
            <div ref={thumb} class={thumbClassName} style={thumbStyle.value} onMousedown={clickThumbHandler}/>
          </div>
        </Transition>);
        };
    },
});
export default Thumb;
